/**
 * GWP Freebie manager — Safe-sync version (paste to replace existing GWP script)
 *
 * - Waits for /cart.js chatter to settle before mutating the cart.
 * - Re-checks server cart state before doing removals/adds to avoid loops.
 * - Tracks last-applied operations to throttle repeated changes.
 *
 * + Publishes a normalized FoxTheme pubsub cartUpdate payload including a robust
 *   item_count so components like <cart-count> (which listen to
 *   FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate) receive a trustworthy number.
 */

(function () {
  "use strict";

  // === GWP Local Mutation Fence ===
  let _gwpMutationInProgress = false;

  async function runWithGwpFence(fn) {
    if (_gwpMutationInProgress) {
      if (GWP_DEBUG)
        console.debug("[gwp-fence] mutation already in progress, skipping");
      return;
    }
    _gwpMutationInProgress = true;
    try {
      await fn();
    } catch (err) {
      console.error("[gwp-fence] error during mutation", err);
    } finally {
      _gwpMutationInProgress = false;
    }
  }

  // === CONFIG ===
  const MIN_PRICE_1 = 1000; // currency units
  const MIN_PRICE_2 = 5000;

  // Variant IDs (numeric) of the freebie product variants you will add as gifts
  const FREEBIE_1 = 55725927334257;
  const FREEBIE_2 = 55744202670449;

  // Allow manual adding of freebie variants? (true = allowed, false = block)
  const ALLOW_MANUAL_FREEBIE_ADDS = true;

  // Debug: set to true while testing to see detailed logs
  const GWP_DEBUG = false;

  // Work in cents to match Shopify's final_line_price (which is cents)
  const MIN_PRICE_CENTS = {
    [MIN_PRICE_1]: MIN_PRICE_1 * 100,
    [MIN_PRICE_2]: MIN_PRICE_2 * 100,
  };

  const FREEBIES_BY_MIN = {
    [MIN_PRICE_1]: FREEBIE_1,
    [MIN_PRICE_2]: FREEBIE_2,
  };

  const GWP_TAG_KEY = "gwp"; // case-insensitive search in product tags

  // === STATE ===
  let _productCache = new Map();
  let _debounceTimer = null;
  let _isRunning = false;

  // Track last /cart.js fetch timestamp to detect chatter/settling
  window._gwpLastCartJsFetchAt = window._gwpLastCartJsFetchAt || 0;

  // Throttle repeated applied actions to avoid loops
  let _lastAppliedAction = { id: null, ts: 0 };
  const APPLY_THROTTLE_MS = 4000; // ignore identical actions within this window

  // Precomputed helpers
  const FREEBIE_IDS = Object.values(FREEBIES_BY_MIN).map(Number);
  const FREEBIE_ID_FOR_MIN = {};
  Object.keys(FREEBIES_BY_MIN).forEach((k) => {
    FREEBIE_ID_FOR_MIN[Number(k)] = Number(FREEBIES_BY_MIN[k]);
  });

  // === NEW: helpers for publishing normalized cart updates ===

  function normalizedItemCount(cart) {
    // Prefer cart.item_count if it's a number
    try {
      if (!cart) return 0;
      if (typeof cart.item_count === "number" && Number.isFinite(cart.item_count)) {
        return Math.max(0, Math.floor(cart.item_count));
      }
      // If items array exists, sum quantities if present, otherwise use items.length
      if (Array.isArray(cart.items)) {
        const hasQty = cart.items.some((it) => it && typeof it.quantity === "number");
        if (hasQty) {
          return cart.items.reduce((s, it) => {
            const q = it && typeof it.quantity === "number" ? Math.floor(Number(it.quantity) || 0) : 0;
            return s + Math.max(0, q);
          }, 0);
        }
        return cart.items.length;
      }
      // fallback to total_items-ish fields
      if (typeof cart.total_items === "number" && Number.isFinite(cart.total_items)) {
        return Math.max(0, Math.floor(cart.total_items));
      }
      return 0;
    } catch (e) {
      return 0;
    }
  }

  function publishCartUpdate(cart) {
    try {
      if (!cart || typeof cart !== "object") return;
      const item_count = normalizedItemCount(cart);
      const payload = { cart: Object.assign({}, cart, { item_count }) };

      // Primary path: FoxTheme pubsub if available
      try {
        if (window.FoxTheme && FoxTheme.pubsub && typeof FoxTheme.pubsub.publish === "function") {
          // Use the same event key the theme expects. Guard existence of the constant.
          const ev =
            FoxTheme.pubsub.PUB_SUB_EVENTS && FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate
              ? FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate
              : "cartUpdate";
          FoxTheme.pubsub.publish(ev, payload);
          if (GWP_DEBUG) console.debug("[gwp] published FoxTheme pubsub cartUpdate", payload);
          return;
        }
      } catch (e) {
        if (GWP_DEBUG) console.error("[gwp] FoxTheme publish failed", e);
      }

      // Fallback: dispatch a DOM event so other code can hook it if needed
      try {
        document.dispatchEvent(new CustomEvent("gwp:cart-update", { detail: payload }));
        if (GWP_DEBUG) console.debug("[gwp] dispatched gwp:cart-update", payload);
      } catch (e) {
        if (GWP_DEBUG) console.error("[gwp] fallback dispatch failed", e);
      }
    } catch (err) {
      if (GWP_DEBUG) console.error("[gwp] publishCartUpdate error", err);
    }
  }

  // === UTIL (Shopify cart helpers) ===
  async function getCart() {
    const res = await fetch("/cart.js", { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return await res.json();
  }
  async function getCartItems() {
    const cart = await getCart();
    return cart.items || [];
  }

  async function fetchProduct(handle) {
    if (_productCache.has(handle)) return _productCache.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`, {
        credentials: "same-origin",
      });
      if (!res.ok) throw new Error(`Failed fetching product ${handle}`);
      const product = await res.json();
      _productCache.set(handle, product);
      return product;
    } catch (err) {
      console.error("fetchProduct error:", err);
      const fallback = { tags: [] };
      _productCache.set(handle, fallback);
      return fallback;
    }
  }

  async function fetchProductsForHandles(handles) {
    const unique = Array.from(new Set(handles));
    const promises = unique.map((h) => fetchProduct(h));
    const products = await Promise.all(promises);
    const map = new Map();
    unique.forEach((h, i) => map.set(h, products[i]));
    return map;
  }

  async function postCart(method, payload) {
    try {
      const res = await fetch(
        `${window.Shopify.routes.root}cart/${method}.js`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Cart ${method} failed: ${res.status} ${text}`);
      }
      const data = await res.json();

      // === Trigger events for reactive updates ===
      try {
        // For the drawer (existing behavior)
        document.dispatchEvent(
          new CustomEvent("cart:refresh", { detail: { open: true } })
        );

        // For main-cart: trigger grouped sections re-render
        const mainCart = document.querySelector("main-cart");
        if (mainCart) {
          const sectionId = window.FoxTheme?.utils?.getSectionId(mainCart);
          if (sectionId) {
            document.dispatchEvent(
              new CustomEvent("cart:grouped-sections", {
                detail: { sections: [sectionId] },
              })
            );
          }
        }
      } catch (e) {
        if (GWP_DEBUG) console.error("postCart event dispatch error", e);
      }

      // === NEW: publish normalized cart update for pubsub listeners ===
      try {
        publishCartUpdate(data);
      } catch (e) {
        if (GWP_DEBUG) console.error("postCart publishCartUpdate failed", e);
      }

      return data;
    } catch (err) {
      console.error("postCart error:", err);
      throw err;
    }
  }

  async function removeAllFreebies() {
    const cartItems = await getCartItems();
    const freebies = cartItems.filter(
      (it) => it.properties && "freebie" in it.properties
    );
    if (!freebies.length) return;
    await Promise.all(
      freebies.map((it) => postCart("change", { id: it.key, quantity: 0 }))
    );
  }

  // === Helpers: DOM & variant id extraction ===
  
  function getItemVariantId(it) {
  // pick the most likely variant id fields consistently
  return Number(it?.variant_id ?? it?.variantId ?? it?.variant ?? it?.id ?? 0);
}

function isGenuineFreebie(it) {
  try {
    if (!it) return false;
    // explicit freebie property is authoritative
    if (it.properties && "freebie" in it.properties) return true;

    // if the variant is a configured freebie variant, only treat as freebie
    // when line price is 0 (conservative fallback).
    const vid = getItemVariantId(it);
    if (!FREEBIE_IDS.includes(vid)) return false;

    // final_line_price (Shopify) is in cents; fall back to other fields if needed
    const priceCents = Number(it.final_line_price ?? it.final_price ?? it.price ?? 0);
    return priceCents === 0;
  } catch (e) {
    return false;
  }
}


  function getDomVariantIds() {
    const nodes = Array.from(
      document.querySelectorAll("[data-quantity-variant-id]")
    );
    const ids = nodes
      .map((n) => {
        try {
          return Number(n.getAttribute("data-quantity-variant-id"));
        } catch (e) {
          return NaN;
        }
      })
      .filter(Boolean);
    return Array.from(new Set(ids));
  }

  function extractVariantIdsFromCartJson(cartJson) {
    if (!cartJson || !Array.isArray(cartJson.items)) return [];
    return cartJson.items
      .map((it) =>
        Number(it.variant_id || it.variantId || it.id || it.variant || 0)
      )
      .filter(Boolean);
  }

  function arraysEqualUnordered(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const sa = a.slice().sort((x, y) => x - y);
    const sb = b.slice().sort((x, y) => x - y);
    for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
    return true;
  }

  // Wait for a quiet period of cart.js fetches
  function waitForCartJsQuiet(settleMs = 350, maxWait = 4000) {
    return new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        const sinceLast = Date.now() - (window._gwpLastCartJsFetchAt || 0);
        if (sinceLast >= settleMs) return resolve(true);
        if (Date.now() - start > maxWait) return resolve(false);
        setTimeout(check, Math.max(50, settleMs - sinceLast));
      };
      check();
    });
  }

  // Wait for DOM variant ids to match cart JSON variant ids (limited wait)
  async function waitForDomToMatchCartVariants(
    targetVariantIds,
    timeout = 2200,
    pollMs = 80,
    stableMs = 250
  ) {
    const start = Date.now();
    let lastDom = getDomVariantIds();
    let lastChangeAt = Date.now();
    if (arraysEqualUnordered(lastDom, targetVariantIds)) return true;
    while (Date.now() - start < timeout) {
      await new Promise((r) => setTimeout(r, pollMs));
      const nowDom = getDomVariantIds();
      if (!arraysEqualUnordered(nowDom, lastDom)) {
        lastDom = nowDom;
        lastChangeAt = Date.now();
      } else {
        if (arraysEqualUnordered(nowDom, targetVariantIds)) return true;
        if (Date.now() - lastChangeAt >= stableMs) break;
      }
    }
    return false;
  }

  // Safe apply: only perform cart mutations after cart.js quiet + re-checking state, throttle duplicates
  async function safeApplyFreebieChanges(
    removals /* array of {id (line key)} */,
    addVariantId /* number or null */,
    freebieValueStr /* string or null */
  ) {
    try {
      const actionSignature = JSON.stringify({
        removals: removals.map((r) => r.id || r.key || r),
        addVariantId,
        freebieValueStr,
      });
      const now = Date.now();
      if (
        _lastAppliedAction.id === actionSignature &&
        now - _lastAppliedAction.ts < APPLY_THROTTLE_MS
      ) {
        if (GWP_DEBUG)
          console.debug(
            "[gwp-safeApply] skipping duplicate action (throttled)",
            actionSignature
          );
        return;
      }

      if (GWP_DEBUG)
        console.debug(
          "[gwp-safeApply] waiting for cart.js quiet before applying",
          actionSignature
        );

      // 1) wait for cart activity to quiet
      await waitForCartJsQuiet(350, 4500);

      // 2) re-fetch cart and compute current freebies to avoid duplicates / racing
      const cart = await getCart();
      const currentVariantIds = extractVariantIdsFromCartJson(cart);
      const currentFreebies = (cart.items || []).filter((it) => isGenuineFreebie(it));

      const hasDesired = currentFreebies.some(
        (it) =>
          (it.properties &&
            String(it.properties.freebie) === freebieValueStr) ||
          (Number(it.id) === Number(addVariantId) &&
            it.properties &&
            "freebie" in it.properties &&
            String(it.properties.freebie) === freebieValueStr)
      );

      // If after waiting the server/cart already reflects desired state, skip
      if (addVariantId && hasDesired) {
        if (GWP_DEBUG)
          console.debug(
            "[gwp-safeApply] desired freebie already present after quiet — nothing to do"
          );
        _lastAppliedAction = { id: actionSignature, ts: now };
        return;
      }

      // Prepare final removals (line keys may have changed) by re-checking cart items keys
      const cartItems = cart.items || [];
      const finalRemovals = [];
      for (const r of removals || []) {
        // r may be object with id/key or key
        const keyToRemove = r.id || r.key || r;
        // try to map to current line key if variant id matches
        const found = cartItems.find(
          (it) =>
            it.key === keyToRemove ||
            String(it.id) === String(keyToRemove) ||
            String(it.variant_id) === String(keyToRemove)
        );
        if (found) finalRemovals.push(found.key);
      }

      // If nothing to remove and nothing to add, exit
      if (!finalRemovals.length && (!addVariantId || hasDesired)) {
        if (GWP_DEBUG)
          console.debug(
            "[gwp-safeApply] nothing to remove or add after re-check"
          );
        _lastAppliedAction = { id: actionSignature, ts: now };
        return;
      }

      // Execute removals first
      if (finalRemovals.length) {
        if (GWP_DEBUG)
          console.debug("[gwp-safeApply] removing keys", finalRemovals);
        await Promise.all(
          finalRemovals.map((key) =>
            postCart("change", { id: key, quantity: 0 })
          )
        );
        // after removals, let cart settle
        await waitForCartJsQuiet(300, 3500);
      }

      // Re-check again before adding
      const afterRemovalsCart = await getCart();
      const stillHasDesired = (afterRemovalsCart.items || []).some(
        (it) =>
          it.properties && String(it.properties.freebie) === freebieValueStr
      );
      if (addVariantId && !stillHasDesired) {
        if (GWP_DEBUG)
          console.debug(
            "[gwp-safeApply] adding freebie variant",
            addVariantId,
            "with freebie prop",
            freebieValueStr
          );
        await postCart("add", {
          items: [
            {
              id: Number(addVariantId),
              quantity: 1,
              properties: { freebie: String(freebieValueStr) },
            },
          ],
        });
        // wait for final settle
        await waitForCartJsQuiet(350, 3500);
      } else {
        if (GWP_DEBUG)
          console.debug(
            "[gwp-safeApply] no add required after removals (already present)"
          );
      }

      _lastAppliedAction = { id: actionSignature, ts: Date.now() };
    } catch (e) {
      console.error("gwp safeApply error", e);
    }
  }

  // === CORE LOGIC (checkGWP) largely same but using safeApplyFreebieChanges to perform cart mutations ===
  async function checkGWP() {
    if (_isRunning) return;
    _isRunning = true;
    try {
      const cartItems = await getCartItems();
      if (GWP_DEBUG)
        console.debug(
          "[gwp] checkGWP start cartItems",
          JSON.parse(JSON.stringify(cartItems))
        );
      if (!cartItems.length) {
        await removeAllFreebies();
        return;
      }

      // compute GWP total (in cents) using product tags
      const handles = cartItems.map((i) => i.handle).filter(Boolean);
      const productMap = await fetchProductsForHandles(handles);

      const totalGWPPriceCents = cartItems.reduce((sum, item) => {
        const product = productMap.get(item.handle) || { tags: [] };
        const hasGWP = (product.tags || []).some((t) =>
          String(t).toLowerCase().includes(GWP_TAG_KEY)
        );
        return hasGWP ? sum + Number(item.final_line_price || 0) : sum;
      }, 0);

      if (GWP_DEBUG)
        console.debug("[gwp] totalGWPPriceCents", totalGWPPriceCents);

      // decide desiredMinSpend
      let desiredMinSpend = null;
      if (totalGWPPriceCents >= MIN_PRICE_CENTS[MIN_PRICE_2])
        desiredMinSpend = MIN_PRICE_2;
      else if (totalGWPPriceCents >= MIN_PRICE_CENTS[MIN_PRICE_1])
        desiredMinSpend = MIN_PRICE_1;

      // discover freebie-like lines: either have properties.freebie or are a FREEBIE variant
      const existingFreebies = cartItems.filter((it) => isGenuineFreebie(it));

      // map by variant -> duplicates
      const freebiesByVariant = existingFreebies.reduce((m, it) => {
  const vid = getItemVariantId(it);
  if (!m[vid]) m[vid] = [];
  m[vid].push(it);
  return m;
}, {});


      // remove duplicate lines for same variant (keep first)
      const duplicateRemovalIds = [];
      Object.keys(freebiesByVariant).forEach((variantIdStr) => {
        const lines = freebiesByVariant[variantIdStr];
        if (lines.length > 1) {
          for (let i = 1; i < lines.length; i++)
            duplicateRemovalIds.push({ id: lines[i].key });
        }
      });

      if (duplicateRemovalIds.length) {
        // use safe apply for duplicate removals, then re-run
        await safeApplyFreebieChanges(duplicateRemovalIds, null, null);
        _isRunning = false;
        return runWithGwpFence(() => checkGWP());
      }

      // re-fetch for up-to-date freebies
      const updatedCart = await getCartItems();
      const updatedExistingFreebies = updatedCart.filter((it) => isGenuineFreebie(it));


      const updatedExistingValues = updatedExistingFreebies
        .map((it) =>
          it.properties && "freebie" in it.properties
            ? String(it.properties.freebie)
            : null
        )
        .filter(Boolean);

      const desiredValueStr = desiredMinSpend ? String(desiredMinSpend) : null;

      const onlyDesiredExists =
        desiredMinSpend &&
        updatedExistingValues.length === 1 &&
        updatedExistingValues[0] === desiredValueStr;
      if (onlyDesiredExists) {
        if (GWP_DEBUG)
          console.debug("[gwp] only desired freebie exists — nothing to do");
        return;
      }

      // Prepare removals (all freebies that are not desired)
      const removals = [];
      updatedExistingFreebies.forEach((it) => {
        // Only remove if it's a genuine freebie line (properties.freebie exists)
        if (it.properties && "freebie" in it.properties) {
          const val = String(it.properties.freebie);
          if (!desiredValueStr || val !== desiredValueStr) {
            removals.push({ id: it.key });
          }
        }
      });


      // Add desired freebie if necessary
      if (desiredMinSpend) {
        const alreadyHasDesired = updatedExistingFreebies.some(
          (it) =>
            it.properties && String(it.properties.freebie) === desiredValueStr
        );
        const freebieVariantId = FREEBIE_ID_FOR_MIN[desiredMinSpend];

        if (!alreadyHasDesired) {
          if (freebieVariantId) {
            // Use safeApply which waits for theme to settle before applying removals and then add
            await safeApplyFreebieChanges(
              removals,
              freebieVariantId,
              desiredValueStr
            );
            return;
          } else {
            console.warn(
              "No freebie variant id configured for min spend",
              desiredMinSpend
            );
            await safeApplyFreebieChanges(removals, null, null);
            return;
          }
        } else {
          // already has desired freebies; remove extras safely
          await safeApplyFreebieChanges(removals, null, null);
          return;
        }
      } else {
        // desiredMinSpend is null -> remove all freebies
        if (removals.length) {
          await safeApplyFreebieChanges(removals, null, null);
        }
        return;
      }
    } catch (err) {
      console.error("checkGWP error:", err);
    } finally {
      _isRunning = false;
    }
  }

  // === DEBOUNCE BINDING ===
  function scheduleCheckGWP(delay = 250) {
    if (_debounceTimer) clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(() => {
      _debounceTimer = null;
      // old: checkGWP().catch(err => console.error(err));
      runWithGwpFence(() => checkGWP()).catch((err) => console.error(err));
    }, delay);
  }

  // Listen for cart updates; debounce to avoid rapid repeated calls
  document.addEventListener("cart:updated", () => scheduleCheckGWP());
  document.addEventListener("cart:refresh", () => scheduleCheckGWP());
  document.addEventListener("DOMContentLoaded", () => scheduleCheckGWP(50));

  // === CLIENT-SIDE GUARDS (only when ALLOW_MANUAL_FREEBIE_ADDS === false) ===
  if (!ALLOW_MANUAL_FREEBIE_ADDS) {
    document.addEventListener("submit", function (ev) {
      try {
        const form = ev.target;
        if (!form || !form.matches || !form.matches('form[action="/cart/add"]'))
          return;
        const variantInput = form.querySelector(
          'input[name="id"], select[name="id"]'
        );
        if (!variantInput) return;
        const variantId = Number(variantInput.value);
        if (FREEBIE_IDS.includes(variantId)) {
          ev.preventDefault();
          window.alert(
            "This item is a free gift and cannot be added manually."
          );
          return false;
        }
      } catch (e) {
        console.error("freebie form guard error:", e);
      }
    });

    (function installFetchGuard() {
      if (!window.fetch) return;
      const originalFetch = window.fetch.bind(window);
      window.fetch = async function (input, init) {
        try {
          let url =
            typeof input === "string" ? input : (input && input.url) || "";
          if (!url && input && input instanceof Request) url = input.url;
          if (typeof url === "string" && url.includes("/cart/add.js")) {
            try {
              if (init && init.body && typeof init.body === "string") {
                const parsed = JSON.parse(init.body);
                let blocked = false;
                if (parsed && parsed.items && Array.isArray(parsed.items)) {
                  for (const it of parsed.items) {
                    const id = Number(it.id);
                    const props = it.properties || {};
                    const hasFreebieProp = props && "freebie" in props;
                    if (FREEBIE_IDS.includes(id) && !hasFreebieProp)
                      blocked = true;
                  }
                } else if (parsed && parsed.id) {
                  const id = Number(parsed.id);
                  const props = parsed.properties || {};
                  const hasFreebieProp = props && "freebie" in props;
                  if (FREEBIE_IDS.includes(id) && !hasFreebieProp)
                    blocked = true;
                }
                if (blocked) {
                  window.alert(
                    "This item is a free gift and cannot be added manually."
                  );
                  return Promise.reject(
                    new Error("Manual add of freebie variant blocked by theme.")
                  );
                }
              }
              if (
                init &&
                init.body &&
                typeof init.body === "object" &&
                typeof init.body.get === "function"
              ) {
                const fd = init.body;
                const idVal = fd.get("id") || fd.get("variantId");
                if (idVal) {
                  const id = Number(idVal);
                  const hasFreebieProp = fd.get("properties[freebie]") !== null;
                  if (FREEBIE_IDS.includes(id) && !hasFreebieProp) {
                    window.alert(
                      "This item is a free gift and cannot be added manually."
                    );
                    return Promise.reject(
                      new Error(
                        "Manual add of freebie variant blocked by theme."
                      )
                    );
                  }
                }
              }
            } catch (e) {
              /* fall through */
            }
          }
        } catch (outerErr) {
          console.error("fetch guard error", outerErr);
        }
        return originalFetch(input, init);
      };
    })();
  }

  // === WATCH /cart.js traffic timestamp without overriding other wrappers ===
  (function installCartJsTimestampWatcher() {
    if (window._gwpCartJsTimestampWatcherInstalled) return;
    window._gwpCartJsTimestampWatcherInstalled = true;

    // Wrap fetch minimally to update timestamp when /cart.js responses arrive
    try {
      if (window.fetch) {
        const prevFetch = window.fetch.bind(window);
        window.fetch = async function (input, init) {
          const url =
            typeof input === "string" ? input : (input && input.url) || "";
          const res = await prevFetch(input, init);
          try {
            if (
              typeof url === "string" &&
              url.includes("/cart.js") &&
              res &&
              res.ok
            ) {
              window._gwpLastCartJsFetchAt = Date.now();
              if (GWP_DEBUG)
                console.debug(
                  "[gwp-ts] /cart.js fetch at",
                  new Date(window._gwpLastCartJsFetchAt).toISOString()
                );

              // Try to read the cart JSON (without consuming the original response),
              // and publish a normalized cartUpdate payload so other components like CartCount get item_count.
              try {
                // res.clone() is safe in browsers that support it
                const cloned = res.clone();
                cloned.json().then((cartJson) => {
                  try {
                    publishCartUpdate(cartJson);
                  } catch (e) {
                    if (GWP_DEBUG) console.error("[gwp] publishCartUpdate failed on fetch clone", e);
                  }
                }).catch(() => {
                  /* ignore non-json or parse errors */
                });
              } catch (e) {
                if (GWP_DEBUG) console.error("[gwp] res.clone() publish failed", e);
              }
            }
          } catch (e) {}
          return res;
        };
      }
    } catch (e) {
      console.error("installCartJsTimestampWatcher fetch wrap failed", e);
    }

    // XHR watcher to also update timestamp for cart-related XHRs
    try {
      if (window.XMLHttpRequest) {
        const origOpen = XMLHttpRequest.prototype.open;
        const origSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function (method, url) {
          try {
            this._gwpOpenUrl = url;
          } catch (e) {}
          return origOpen.apply(this, arguments);
        };
        XMLHttpRequest.prototype.send = function (body) {
          this.addEventListener("load", function () {
            try {
              const url = this._gwpOpenUrl || "";
              if (typeof url === "string" && url.includes("/cart")) {
                window._gwpLastCartJsFetchAt = Date.now();
                if (GWP_DEBUG)
                  console.debug(
                    "[gwp-ts] XHR cart call at",
                    new Date(window._gwpLastCartJsFetchAt).toISOString()
                  );

                // Try to parse responseText as JSON and publish normalized cartUpdate
                try {
                  const txt = this.responseText;
                  if (txt) {
                    const json = JSON.parse(txt);
                    if (json && typeof json === "object" && (Array.isArray(json.items) || typeof json.item_count === "number")) {
                      publishCartUpdate(json);
                    }
                  }
                } catch (e) {
                  // ignore parse errors
                }
              }
            } catch (e) {}
          });
          return origSend.apply(this, arguments);
        };
      }
    } catch (e) {
      console.error("installCartJsTimestampWatcher XHR wrap failed", e);
    }
  })();

  // === OPTIONAL: public helper to force a sync check ===
  window.gwpCheckNow = function () {
    scheduleCheckGWP(0);
  };

  // Expose internals for debugging (optional)
  window._gwp = {
    checkGWP,
    scheduleCheckGWP,
    FREEBIE_IDS: FREEBIE_IDS.slice(),
    MIN_PRICES: Object.keys(FREEBIES_BY_MIN).map(Number),
  };

  
})();
